import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { GoogleOAuthProvider } from "@react-oauth/google";

import App from "./App.jsx";
import "./index.css";
import { GOOGLE_CLIENT_ID } from "./auth/config.js";
import { initSentry } from "./monitoring/sentry.js";
import { ErrorScreen } from "./monitoring/ErrorScreen.jsx";

initSentry();

// ============================================================================
// PRODUCTION ENVIRONMENT VALIDATION
// ============================================================================

/**
 * CRITICAL: Production builds MUST NOT use localhost API
 * 
 * This guard ensures:
 * 1. VITE_API_URL is configured in production
 * 2. No localhost references in production builds
 * 3. Railway backend is the only production target
 * 
 * If this throws, deployment should FAIL.
 */
if (import.meta.env.PROD) {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const effectiveApiUrl = apiUrl || '/api';
  const isSameOriginProxy = effectiveApiUrl === '/api' || effectiveApiUrl.startsWith('/');
  
  // Check 1: API URL must be configured
  if (!effectiveApiUrl && !isSameOriginProxy) {
    throw new Error(
      '\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '🚨 PRODUCTION BUILD ERROR\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '\n' +
      'VITE_API_URL environment variable not configured\n' +
      '\n' +
      'Required for production deployment:\n' +
      '  VITE_API_URL=/api\n' +
      '\n' +
      'This should point to the same-origin API proxy.\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'
    );
  }
  
  // Check 2: localhost forbidden in production
  if (effectiveApiUrl.includes('localhost') || effectiveApiUrl.includes('127.0.0.1')) {
    throw new Error(
      '\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '🚨 PRODUCTION BUILD ERROR\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '\n' +
      'localhost API URL forbidden in production builds\n' +
      '\n' +
      `Current: ${effectiveApiUrl}\n` +
      `Expected: /api or https://api.yourdomain.com\n` +
      '\n' +
      'Production must use Railway backend with Neon database.\n' +
      'localhost is development-only.\n' +
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'
    );
  }
  
  // Check 3: Must be HTTPS unless using same-origin proxy
  if (!isSameOriginProxy && !effectiveApiUrl.startsWith('https://')) {
    console.warn(
      '\n' +
      '⚠️  WARNING: Production API URL should use HTTPS or /api proxy\n' +
      `Current: ${effectiveApiUrl}\n` +
      'Expected: /api or https://api.yourdomain.com\n'
    );
  }
  
  console.log('✅ Production environment validated');
  console.log(`   API URL: ${effectiveApiUrl}`);
  console.log(`   Mode: ${import.meta.env.MODE}`);
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
        <ErrorScreen error={error} resetError={resetError} />
      )}>
        <App />
      </Sentry.ErrorBoundary>
    </GoogleOAuthProvider>
  </React.StrictMode>
);
/* Build: 20260107-224511 */
